/**
 * DRAW — the marking surface, native on @ankr/glass.
 *
 * This is the surface the product turns on: a candidate sketches a technical drawing and is marked
 * against HIS OWN coordinates, so a correct answer drawn small in a corner still scores. Everything
 * else teaches; this is the thing that examines.
 *
 * ── THE ENGINE IS CALLED, NEVER REIMPLEMENTED ─────────────────────────────────────────────────
 * `/api/drawing/check` does the marking, exactly as the standalone /draw page calls it. A second
 * copy of a marking rule is how the rule a candidate is graded by stops being the rule anybody is
 * reading — so nothing here decides a mark, and this file would be wrong the moment it did.
 *
 * ── THE Y FLIP, WHICH IS EASY TO MISS AND SILENT WHEN WRONG ───────────────────────────────────
 * Screen y grows downward; the drawing's y grows upward. The submission therefore sends `h - y`,
 * identically to the page this replaces. Get it wrong and the request still succeeds, the engine
 * still marks, and every answer is simply wrong — a failure with no error attached to it.
 *
 * ── PAPER IS WHITE, DELIBERATELY ──────────────────────────────────────────────────────────────
 * The pad does not follow the theme. An examination answer sheet is white and the hull lines on it
 * are drawn dark, so theming the pad would invert the subject rather than style it. Chrome follows
 * the shell; paper stays paper.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Value, useWidgetData } from '../../../packages/ankr-glass/src/react.jsx';
import { plan } from '../../../packages/ankr-vahana/src/plan.mjs';
import { computed, refused, failed } from '../../../packages/ankr-provenance/src/provenance.mjs';
import { API } from './surfaces.jsx';

const W = {
  list: { slug: 'drawing-list', layout: 'auto-table',
          wire: { kind: 'rest', route: '/api/drawing/list', method: 'GET', field: 'drawings' } },
  spec: { slug: 'drawing-spec', layout: 'auto-table',
          wire: { kind: 'rest', route: '/api/drawing/spec', method: 'GET', field: 'spec' } },
};

/** The pad. Pointer events, so one code path serves finger, stylus and mouse. */
function Pad({ strokes, setStrokes, active, setActive }) {
  const ref = useRef(null);
  const drawing = useRef(null);

  const redraw = useCallback(() => {
    const cv = ref.current; if (!cv) return;
    const dpr = Math.min(devicePixelRatio || 1, 2);
    const w = cv.clientWidth, h = cv.clientHeight;
    if (cv.width !== Math.round(w * dpr)) { cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr); }
    const g = cv.getContext('2d');
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.fillStyle = '#fbfdfb'; g.fillRect(0, 0, w, h);          // paper, not chrome
    g.lineCap = 'round'; g.lineJoin = 'round';
    strokes.forEach((s, i) => {
      g.beginPath();
      s.pts.forEach(([x, y], j) => (j ? g.lineTo(x, y) : g.moveTo(x, y)));
      g.strokeStyle = i === active ? '#0E7FB8' : '#111';
      g.lineWidth = i === active ? 2.6 : 1.9;
      g.stroke();
      if (s.label && s.pts.length) {
        const [lx, ly] = s.pts[Math.floor(s.pts.length / 2)];
        g.fillStyle = '#111'; g.font = '12px system-ui, sans-serif';
        g.fillText(s.label, lx + 6, ly - 6);
      }
    });
  }, [strokes, active]);

  useEffect(() => { redraw(); }, [redraw]);
  useEffect(() => {
    const ro = new ResizeObserver(redraw); if (ref.current) ro.observe(ref.current);
    return () => ro.disconnect();
  }, [redraw]);

  const at = (e) => {
    const r = ref.current.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };

  return (
    <canvas ref={ref} className="draw-pad"
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        drawing.current = { label: '', pts: [at(e)] };
        setStrokes((s) => [...s, drawing.current]);
        setActive(strokes.length);
      }}
      onPointerMove={(e) => {
        if (!drawing.current) return;
        drawing.current.pts.push(at(e));
        setStrokes((s) => [...s.slice(0, -1), { ...drawing.current }]);
      }}
      onPointerUp={() => { drawing.current = null; }}
      onPointerCancel={() => { drawing.current = null; }} />
  );
}

export default function Draw() {
  const list = useWidgetData(W.list, {}, { baseUrl: API, plan });
  const drawings = list.data?.drawings || [];
  const [id, setId] = useState(null);
  const chosen = id || drawings[0]?.id || null;
  const spec = useWidgetData(W.spec, chosen ? { id: chosen } : {},
                             { baseUrl: API, plan, enabled: Boolean(chosen) });

  const [strokes, setStrokes] = useState([]);
  const [active, setActive] = useState(-1);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const padWrap = useRef(null);

  // A new drawing is a new answer. Carrying strokes across would mark one question's work against
  // another's scheme, which the engine would happily do.
  useEffect(() => { setStrokes([]); setActive(-1); setResult(null); }, [chosen]);

  const submit = async () => {
    const h = padWrap.current?.querySelector('canvas')?.clientHeight || 0;
    setBusy(true); setResult(null);
    try {
      const r = await fetch(`${API}/api/drawing/check`, {
        method: 'POST', headers: { 'content-type': 'application/json' },
        // y flipped: screen grows down, the drawing grows up. Identical to the page this replaces.
        body: JSON.stringify({ drawing: chosen,
          strokes: strokes.map((s) => ({ label: s.label, pts: s.pts.map(([x, y]) => [x, h - y]) })) }),
      });
      const j = await r.json();
      // A refusal from the engine is an ANSWER, not an error: it says what it could not mark.
      setResult(j.kind === 'marked' ? j : { refused: j.reason || 'the engine did not mark this' });
    } catch (e) {
      setResult({ failed: String(e?.message || e) });
    } finally { setBusy(false); }
  };

  const s = spec.data || {};
  const unlabelled = strokes.filter((x) => !x.label).length;

  return (
    <div className="shell-col g-rise">
      <div className="g-glass shell-panel">
        <div className="g-label">Sketch and be marked</div>
        <select className="g-input" style={{ marginTop: 'var(--g-3)' }} value={chosen || ''}
          onChange={(e) => setId(e.target.value)} aria-label="Choose a drawing">
          {drawings.map((d) => (
            <option key={d.id} value={d.id}>{d.rank} · {d.title} ({d.marks} marks)</option>
          ))}
        </select>

        {spec.envelope ? <div style={{ marginTop: 'var(--g-3)' }}><Value of={spec.envelope} /></div>
          : s.question ? (
            <>
              <p className="g-body draw-question">{s.question}</p>
              {/* The engine's own caveat, shown rather than summarised. */}
              {s.honesty_note ? <div className="draw-note g-dim">{s.honesty_note}</div> : null}
            </>
          ) : null}
      </div>

      <div className="g-glass shell-panel">
        <div className="draw-head">
          <div className="g-label">
            {s.marks ? `${s.marks} marks` : 'Your sketch'}
            {s.view ? ` · ${s.view}` : ''}
          </div>
          <div className="shell-picker">
            <button className="g-btn" disabled={!strokes.length}
              onClick={() => { setStrokes((x) => x.slice(0, -1)); setActive(-1); }}>Undo</button>
            <button className="g-btn" disabled={!strokes.length}
              onClick={() => { setStrokes([]); setActive(-1); setResult(null); }}>Clear</button>
            <button className="g-btn g-btn-primary" disabled={!strokes.length || busy} onClick={submit}>
              {busy ? 'Marking…' : 'Submit for marking'}
            </button>
          </div>
        </div>

        <div ref={padWrap} className="draw-wrap">
          <Pad strokes={strokes} setStrokes={setStrokes} active={active} setActive={setActive} />
        </div>

        {/* NAMING IS THE ANSWER, not decoration. The engine marks members by label, so an unnamed
            stroke scores nothing however well it is drawn — and the surface says so plainly rather
            than letting a candidate submit and wonder why he lost the marks. */}
        {strokes.length > 0 && (
          <div className="draw-labels">
            {strokes.map((st, i) => (
              <div key={i} className={`draw-label-row ${i === active ? 'on' : ''}`}
                onFocus={() => setActive(i)}>
                <span className="g-mono draw-idx">{i + 1}</span>
                <input className="g-input" placeholder="name this member — e.g. tank top"
                  value={st.label}
                  onChange={(e) => setStrokes((x) =>
                    x.map((y, j) => (j === i ? { ...y, label: e.target.value } : y)))} />
              </div>
            ))}
            {unlabelled > 0 && (
              <div className="draw-warn g-body">
                <Value of={refused(`${unlabelled} stroke${unlabelled === 1 ? '' : 's'} not named. ` +
                  `The engine marks members by name, so an unnamed stroke scores nothing however ` +
                  `well it is drawn.`)} />
              </div>
            )}
          </div>
        )}
      </div>

      {result && (
        <div className="g-glass shell-panel g-rise">
          <div className="g-label">Marked</div>
          {result.refused ? <div style={{ marginTop: 'var(--g-2)' }}><Value of={refused(result.refused)} /></div>
           : result.failed ? <div style={{ marginTop: 'var(--g-2)' }}><Value of={failed(result.failed)} /></div>
           : (
            <>
              <div className="draw-score">
                <span className="draw-awarded">{result.awarded}</span>
                <span className="g-dim"> of {result.of ?? s.marks}</span>
              </div>
              {/* Every line the engine returned, in its own words. Summarising a mark scheme is how a
                  candidate ends up studying our paraphrase instead of the scheme. */}
              <ul className="draw-lines">
                {(result.lines || result.detail || []).map((l, i) => (
                  <li key={i} className="g-body">
                    {typeof l === 'string' ? l : `${l.member ?? ''} ${l.awarded ?? ''}${
                      l.of ? ` of ${l.of}` : ''}${l.why ? ` — ${l.why}` : ''}`}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
